import type { TradeBriefingTimberDisplayRow } from './tradeBriefingScope';

export type TradeBriefingTimberSortKey =
  | 'structure'
  | 'name'
  | 'size'
  | 'length'
  | 'quantity'
  | 'species'
  | 'grade'
  | 'dryness';

export type TradeBriefingTimberSortDir = 'asc' | 'desc';

export function parseTradeBriefingLengthInches(length: string): number {
  const text = (length || '').trim();
  if (!text || text === '—') return 0;
  const feetInches = text.match(/^(\d+(?:\.\d+)?)\s*'\s*(\d+(?:\.\d+)?)?"?$/);
  if (feetInches) {
    return Number(feetInches[1]) * 12 + Number(feetInches[2] || 0);
  }
  const feetOnly = text.match(/^(\d+(?:\.\d+)?)\s*'$/);
  if (feetOnly) return Number(feetOnly[1]) * 12;
  const inchesOnly = text.match(/^(\d+(?:\.\d+)?)\s*"$/);
  if (inchesOnly) return Number(inchesOnly[1]);
  const bare = Number(text.replace(/[^\d.]+/g, ''));
  return Number.isFinite(bare) ? bare : 0;
}

export function parseTradeBriefingSizeSortValue(size: string): [number, number] {
  const match = (size || '').match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
  if (!match) return [0, 0];
  const a = Number(match[1]);
  const b = Number(match[2]);
  return a <= b ? [a, b] : [b, a];
}

function rowText(row: TradeBriefingTimberDisplayRow, key: TradeBriefingTimberSortKey): string {
  if (key === 'structure') return row.structureName;
  if (key === 'name') return row.name;
  if (key === 'size') return row.size;
  if (key === 'length') return row.length;
  if (key === 'species') return row.species;
  if (key === 'grade') return row.grade;
  if (key === 'dryness') return row.dryness;
  return '';
}

export function filterTradeBriefingTimberRows(
  rows: TradeBriefingTimberDisplayRow[],
  filters: { query?: string; structure?: string },
): TradeBriefingTimberDisplayRow[] {
  const query = (filters.query || '').trim().toLowerCase();
  const structure = (filters.structure || '').trim().toLowerCase();
  return rows.filter(row => {
    if (structure && row.structureName.trim().toLowerCase() !== structure) return false;
    if (!query) return true;
    const hay = [
      row.structureName,
      row.name,
      row.size,
      row.length,
      String(row.quantity),
      row.species,
      row.grade,
      row.dryness,
    ]
      .join(' ')
      .toLowerCase();
    return hay.includes(query);
  });
}

export function sortTradeBriefingTimberRows(
  rows: TradeBriefingTimberDisplayRow[],
  key: TradeBriefingTimberSortKey,
  dir: TradeBriefingTimberSortDir,
): TradeBriefingTimberDisplayRow[] {
  const sign = dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    if (key === 'quantity') {
      return (a.quantity - b.quantity) * sign;
    }
    if (key === 'length') {
      const diff = parseTradeBriefingLengthInches(a.length) - parseTradeBriefingLengthInches(b.length);
      return (diff || rowText(a, 'length').localeCompare(rowText(b, 'length'), undefined, { sensitivity: 'base' })) * sign;
    }
    if (key === 'size') {
      const [aMin, aMax] = parseTradeBriefingSizeSortValue(a.size);
      const [bMin, bMax] = parseTradeBriefingSizeSortValue(b.size);
      const diff = aMin - bMin || aMax - bMax;
      return (diff || rowText(a, 'size').localeCompare(rowText(b, 'size'), undefined, { sensitivity: 'base' })) * sign;
    }
    return rowText(a, key).localeCompare(rowText(b, key), undefined, { sensitivity: 'base' }) * sign;
  });
}

export function nextTradeBriefingTimberSort(
  currentKey: TradeBriefingTimberSortKey,
  currentDir: TradeBriefingTimberSortDir,
  nextKey: TradeBriefingTimberSortKey,
): { key: TradeBriefingTimberSortKey; dir: TradeBriefingTimberSortDir } {
  if (currentKey !== nextKey) {
    return { key: nextKey, dir: 'asc' };
  }
  return { key: currentKey, dir: currentDir === 'asc' ? 'desc' : 'asc' };
}

export function uniqueTradeBriefingStructures(rows: TradeBriefingTimberDisplayRow[]): string[] {
  return Array.from(new Set(rows.map(row => row.structureName).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}
